import { isEqual, isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { fetchOpinionSubCounterpartyTypesStarted, getJurisdictionSubCounterpartyTypes, getSubCounterpartyScopeFilters, selectSubCounterpartyTypesJurisdiction, selectSubCounterpartyTypesParent } from '../../../admin/sub-counterparty-types/store';
import { IconButton } from '../../../shared/button/IconButton';
import { Group, Tick } from '../../../shared/icons';
import { Toggle } from '../../../shared/toggle';
import { CustomTooltip, IconTooltip, OverflowTooltip, TooltipList } from '../../../shared/tooltip';
import { SingleField } from '../fields/SingleField';
import { SignOffConversationIcon } from '../sign-off';
import { getFieldSignOffNotes, getIsFirstTimelineEntry, getOpenFieldsAndSections, getShowUpdatedOnly, getSubCounterpartyTypeModalOpen, toggleOpenFieldSection, toggleSubCounterpartyModal, updateCounterpartyCoverageValue } from '../store';
import { CounterpartyCoverage, UpdatedOpinionField } from '../store/types';
import styles from './OpinionSection.module.scss';
import { SectionWrapper } from './SectionWrapper';
import { SubCounterpartyConfigModal } from './SubCounterpartyConfigModal';

const { lightGrey, amethyst, grey, french, primary } = styles;

interface CounterpartyCoverageProps {
    jurisdiction: string | null;
    counterpartyCoverage: CounterpartyCoverage;
    isEditing: boolean;
    isUpdating: boolean;
    fieldsUpdated: UpdatedOpinionField[];
    columns?: number[];
    nettingEngineFields: string[];
}

export const CounterpartyCoverageSection: React.FC<CounterpartyCoverageProps> = ({ counterpartyCoverage, isEditing, isUpdating, fieldsUpdated, columns = [0, 1], jurisdiction, nettingEngineFields }) => {
    const dispatch = useAppDispatch();
    const openFieldsAndSections = useAppSelector(getOpenFieldsAndSections);
    const getFieldOpen = useCallback((id: string) => openFieldsAndSections.includes(id), [openFieldsAndSections]);
    const toggleFieldOpen = useCallback((id: string) => dispatch(toggleOpenFieldSection(id)), [dispatch]);
    const sectionId = 'counterpartyCoverage';
    const STANDARD_OPTIONS_FIELD_ID = 'standardOptions';
    const { standardOptions, other } = counterpartyCoverage;
    const signOffConversation = useAppSelector(getFieldSignOffNotes(sectionId));
    const isFirstTimelineEntry = useAppSelector(getIsFirstTimelineEntry);
    const subCounterpartyConfigModalOpen = useAppSelector(getSubCounterpartyTypeModalOpen);
    const jurisdictionalSubCounterpartyTypes = useAppSelector(getJurisdictionSubCounterpartyTypes);
    const scopeFilters = useAppSelector(getSubCounterpartyScopeFilters);
    const showUpdatedOnly = useAppSelector(getShowUpdatedOnly);

    const getSubCounterparties = useCallback((includedSubCounterpartyTypeIds: number[] | undefined) => jurisdictionalSubCounterpartyTypes.filter(({ subCounterpartyTypeId, scopes }) => !isUndefined(subCounterpartyTypeId) && (includedSubCounterpartyTypeIds || []).includes(subCounterpartyTypeId) && scopes.some(scope => scopeFilters.includes(scope))).map(({ subCounterpartyName }) => subCounterpartyName), [jurisdictionalSubCounterpartyTypes, scopeFilters]);

    const openConfigModal = useCallback(() => dispatch(toggleSubCounterpartyModal(true)), [dispatch]);
    const closeConfigModal = useCallback(() => {
        dispatch(toggleSubCounterpartyModal(false));
        dispatch(selectSubCounterpartyTypesParent(null));
    }, [dispatch]);

    useEffect(() => {
        if (jurisdiction) {
            dispatch(fetchOpinionSubCounterpartyTypesStarted(jurisdiction));
        }
    }, [dispatch, jurisdiction]);

    const updateValue = (value: boolean[], index: number) => dispatch(updateCounterpartyCoverageValue(sectionId, STANDARD_OPTIONS_FIELD_ID, index, value));
    const updateColumnValue = (value: boolean, index: number, columnIndex: number) => {
        let updatedValue = counterpartyCoverage[STANDARD_OPTIONS_FIELD_ID][index].value.map((current, i) => i === columnIndex ? value : current);
        // Edge case: if updating Covered by Opinion to false, also set AET Required to false
        if (columnIndex === 0 && !value) {
            updatedValue = [false, false];
        }
        updateValue(updatedValue, index);
    };

    const aetColumnDisabled = useCallback((fieldIndex: number) => {
        // Disable AET column if it is not selected in Covered by Opinion column
        const coveredByOpinionValue = counterpartyCoverage[STANDARD_OPTIONS_FIELD_ID][fieldIndex].value[0];
        return !coveredByOpinionValue;
    }, [counterpartyCoverage]);

    const fieldUpdatedByInstance = useCallback((index: number, columnIndex: number) => !!fieldsUpdated.find(field => isEqual({ fieldId: field.fieldId, sectionId: field.sectionId, index: field.index, columnIndex: field.columnIndex }, { fieldId: STANDARD_OPTIONS_FIELD_ID, sectionId, index, columnIndex })), [fieldsUpdated, sectionId]);
    const getIsDisabled = useCallback((index: number, columnIndex: number) => {
        const aetDisabledBecauseOfCoverage = columnIndex === 1 && aetColumnDisabled(index);
        return aetDisabledBecauseOfCoverage || (!isEditing && !(isUpdating && fieldUpdatedByInstance(index, columnIndex)));
    }, [isUpdating, isEditing, fieldUpdatedByInstance, aetColumnDisabled]);
    const subCounterpartyTypesUpdatedByInstance = useCallback((index: number, includedSubCounterpartyTypeIds: number[] | undefined) => !!fieldsUpdated.find(field => !isUndefined(field.columnIndex) && isEqual({ fieldId: field.fieldId, sectionId: field.sectionId, index: field.index, includedSubCounterpartyTypeIds: field.includedSubCounterpartyTypeIds }, { fieldId: STANDARD_OPTIONS_FIELD_ID, sectionId, index, includedSubCounterpartyTypeIds })), [fieldsUpdated, sectionId]);
    const shouldShowFieldUpdated = useCallback((index: number, columnIndex: number) => fieldUpdatedByInstance(index, columnIndex) && !isUpdating && !isEditing && !isFirstTimelineEntry, [isUpdating, isEditing, fieldUpdatedByInstance, isFirstTimelineEntry]);
    const shouldShowSubCounterpartyTypesUpdated = useCallback((index: number, includedSubCounterpartyTypeIds: number[] | undefined) => subCounterpartyTypesUpdatedByInstance(index, includedSubCounterpartyTypeIds) && !isUpdating && !isEditing && !isFirstTimelineEntry, [isUpdating, isEditing, subCounterpartyTypesUpdatedByInstance, isFirstTimelineEntry]);

    const disabled = !isEditing && !isUpdating;

    const getShouldDisplayField = useCallback((index: number, includedSubCounterpartyTypeIds: number[] | undefined) => {
        if (!showUpdatedOnly || isFirstTimelineEntry) {
            return true;
        }
        const coveredByOpinionUpdated = fieldUpdatedByInstance(index, 0);
        const aetRequiredUpdated = fieldUpdatedByInstance(index, 1);
        const subCounterpartiesUpdated = subCounterpartyTypesUpdatedByInstance(index, includedSubCounterpartyTypeIds);
        return [coveredByOpinionUpdated, aetRequiredUpdated, subCounterpartiesUpdated].some(valueUpdated => valueUpdated);
    }, [showUpdatedOnly, isFirstTimelineEntry, fieldUpdatedByInstance, subCounterpartyTypesUpdatedByInstance]);

    const updateAll = (checked: boolean, columnIndex: number) => {
        standardOptions.filter(({ label }) => label !== 'Other').forEach(({ value }, index) => {
            if (columnIndex === 0 && !checked) {
                // Edge case: if updating Covered by Opinion to false, also set AET Required to false
                const updatedValue = [false, false];
                updateValue(updatedValue, index);
            } else if (columnIndex === 1 && !standardOptions[index].value[0]) {
                // Do nothing if trying to set AET Required to true when Covered by Opinion is false
            } else {
                const updatedValue = value.map((current, i) => i === columnIndex ? checked : current);
                updateValue(updatedValue, index);
            }
        });
    };

    const getOptionWrapperStyle = useCallback((index: number, noBorderBottom = false) => {
        const totalEntries = standardOptions.length;
        const numberOfLastRowEntries = totalEntries % 2 || 2;
        const lastRowEntries = Array(numberOfLastRowEntries).fill(totalEntries - numberOfLastRowEntries).map((val, i) => val + i);
        const isLastRow = lastRowEntries.includes(index);
        const isFirstColumn = ((index + 1) % 2) === 1;
        if (!isLastRow && !noBorderBottom) {
            return !isFirstColumn ? { width: 'calc((100% / 2) - 24px)', paddingLeft: '15px', borderLeft: `1px solid ${lightGrey}`, borderBottom: `1px solid ${lightGrey}` } : { width: 'calc((100% / 2) - 10px)', borderBottom: `1px solid ${lightGrey}` };
        }
        return !isFirstColumn ? { width: 'calc((100% / 2) - 24px)', paddingLeft: '15px', borderLeft: `1px solid ${lightGrey}` } : { width: 'calc((100% / 2) - 10px)' };
    }, [standardOptions.length]);

    const boxShadowColour = useCallback((showFieldUpdated: boolean) => {
        if (showFieldUpdated) {
            return amethyst;
        }
        return grey;
    }, []);

    const onColour = useCallback((showFieldUpdated: boolean) => {
        if (showFieldUpdated) {
            return amethyst;
        }
        return french;
    }, []);

    const subCounterpartyIconColour = useCallback((showFieldUpdated: boolean) => showFieldUpdated ? amethyst : primary, []);

    const getAllSelected = useCallback((columnIndex: number) => {
        if (columnIndex === 1) {
            // For AET column, only consider options where Covered by Opinion is true
            const filteredOptions = standardOptions.filter(({ value, label }) => label !== 'Other' && value[0]);
            return filteredOptions.length > 0 && filteredOptions.map(({ value }) => value[columnIndex]).every(value => !!value);
        }
        return standardOptions.filter(({ label }) => label !== 'Other').map(({ value }) => value[columnIndex]).every(value => !!value);
    }, [standardOptions]);

    const handleSubCounterpartyIconClick = useCallback((parentCounterparty?: string) => {
        if (!isNull(jurisdiction)) {
            openConfigModal();
            dispatch(selectSubCounterpartyTypesJurisdiction(jurisdiction));
            if (!isUndefined(parentCounterparty)) {
                dispatch(selectSubCounterpartyTypesParent(parentCounterparty));
            }
        }
    }, [jurisdiction, openConfigModal, dispatch]);

    const getColumnHeaders = useCallback((columnIndex: number) => {
        const optionWrapperStyle = getOptionWrapperStyle(columnIndex, true);
        return (
            <div className={styles.columnHeaders} style={optionWrapperStyle} key={columnIndex}>
                <div className={styles.columnHeader}>
                    <IconTooltip icon={Tick} content='Covered by Opinion?' fontSize={20} />
                </div>
                <div className={styles.columnHeader}>
                    <CustomTooltip overlayText='AET Required?'>
                        <div className={styles.aetHeader}>AET</div>
                    </CustomTooltip>
                </div>
                <div className={styles.subCounterpartyTypesHeader}>
                    <IconButton icon={Group} overlayText='Included Sub Counterparty Types' fontSize={20} onClick={() => handleSubCounterpartyIconClick()} />
                </div>
            </div>
        );
    }, [getOptionWrapperStyle, handleSubCounterpartyIconClick]);

    const nettingEngineTooltip = 'Any counterparties selected from this section are considered valid in the Netting Engine analysis';

    return (
        <SectionWrapper id={sectionId} label='Counterparty Coverage' isEditing={isEditing} isUpdating={isUpdating} nettingEngineFields={nettingEngineFields} nettingEngineTooltip={nettingEngineTooltip}>
            <div className={styles.counterpartyCoverageSectionWrapper}>
                <div className={styles.counterpartyCoverageWrapper}>
                    <div className={styles.counterpartyCoverageHeaderWrapper}>
                        <div className={styles.selectAllWrapper}>
                            <div className={styles.selectAllLabel}>{disabled ? 'All Selected' : 'Select All'}</div>
                            <div className={styles.multiToggleWrapper}>
                                {columns.map(columnIndex => {
                                    const allSelected = getAllSelected(columnIndex);
                                    return (
                                        <div className={styles.toggleWrapper} key={columnIndex}>
                                            <Toggle onChange={checked => updateAll(checked, columnIndex)} checked={allSelected} disabled={disabled} />
                                        </div>
                                    );
                                })}
                                <div className={styles.subCounterpartyTypesWrapper}></div>
                            </div>
                        </div>
                        <div className={styles.addOptionWrapper}>
                            {!isUndefined(signOffConversation) && <SignOffConversationIcon signOffConversation={signOffConversation} id={sectionId} />}
                        </div>
                    </div>
                    <div className={styles.columnHeaderWrapper}>
                        {columns.map(columnIndex => getColumnHeaders(columnIndex))}
                    </div>
                    <div className={styles.counterpartyCoverageOptionsWrapper} data-testid='counterparty-coverage-standard-options-wrapper'>
                        {standardOptions.map(({ value, label, includedSubCounterpartyTypeIds }, index) => {
                            const subCounterpartyTypes = getSubCounterparties(includedSubCounterpartyTypeIds);
                            const optionWrapperStyle = getOptionWrapperStyle(index);
                            const showSubCounterpartyTypesUpdated = shouldShowSubCounterpartyTypesUpdated(index, includedSubCounterpartyTypeIds);
                            const shouldDisplayField = getShouldDisplayField(index, includedSubCounterpartyTypeIds);

                            if (!shouldDisplayField) {
                                return null;
                            }

                            return (
                                <div className={styles.toggleOptionWrapper} key={index} style={optionWrapperStyle}>
                                    <div className={styles.counterpartyCoverageLabel}>
                                        <OverflowTooltip overlayText={label} trigger='click' />
                                    </div>
                                    <div className={styles.multiToggleWrapper}>
                                        {value.map((val, i) => {
                                            const isDisabled = getIsDisabled(index, i);
                                            const showFieldUpdated = shouldShowFieldUpdated(index, i);
                                            return (
                                                <div className={styles.toggleWrapper} key={i}>
                                                    <Toggle
                                                        onChange={checked => updateColumnValue(checked, index, i)}
                                                        checked={val}
                                                        disabled={isDisabled}
                                                        boxShadowColour={boxShadowColour(showFieldUpdated)}
                                                        onColour={onColour(showFieldUpdated)}
                                                    />
                                                </div>
                                            );
                                        })}
                                        <div className={styles.subCounterpartyTypesWrapper}>
                                            {subCounterpartyTypes.length > 0 &&
                                                <TooltipList overlayText={subCounterpartyTypes}>
                                                    <div className={styles.iconButtonWrapper}>
                                                        <IconButton
                                                            icon={Group}
                                                            fontSize={20}
                                                            color={subCounterpartyIconColour(showSubCounterpartyTypesUpdated)}
                                                            onClick={() => handleSubCounterpartyIconClick(label)}
                                                        />
                                                    </div>
                                                </TooltipList>
                                            }
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <SubCounterpartyConfigModal
                        isOpen={subCounterpartyConfigModalOpen}
                        close={closeConfigModal}
                        readOnly={!isEditing}
                        counterpartyCoverage={counterpartyCoverage}
                        jurisdiction={jurisdiction!}
                    />
                </div>
                <SingleField
                    field={other}
                    fieldId='other'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
            </div>
        </SectionWrapper>
    );
};
